export const capabilityStates = [
  "DISCOVERED",
  "LEARNING",
  "UNDERSTOOD",
  "BUILT",
  "SHIPPED",
  "EVIDENCED",
  "DEFENDED",
] as const;

export type CapabilityState = (typeof capabilityStates)[number];
export type GraphMode = "STACK" | "PROJECT" | "EVIDENCE" | "LEARNING";
export type EdgeRelation =
  | "depends_on"
  | "calls"
  | "stores_in"
  | "accelerates"
  | "deploys_to"
  | "observes"
  | "related";

export interface TechnologyNode {
  id: string;
  slug: string;
  name: string;
  category: string;
  layer: string;
  summary: string;
  whyItExists: string;
  capabilityState: CapabilityState;
  prerequisiteIds: string[];
  relatedNodeIds: string[];
  tags: string[];
  position: { x: number; y: number };
  createdAt: string;
  updatedAt: string;
}

export interface GraphEdge {
  id: string;
  sourceId: string;
  targetId: string;
  relation: EdgeRelation;
  label?: string;
}

export interface Project {
  id: string;
  slug: string;
  name: string;
  repo?: string;
  summary: string;
  status: "idea" | "active" | "shipped" | "archived";
  nodeIds: string[];
  evidenceIds: string[];
  deployUrl?: string;
}

export interface Evidence {
  id: string;
  title: string;
  repo: string;
  type: "code" | "test" | "benchmark" | "deploy" | "pr" | "doc" | "incident" | "demo" | "interview";
  url?: string;
  commitSha?: string;
  nodeIds: string[];
  projectIds: string[];
  observedAt: string;
  verifiedAt?: string;
  verification: "unverified" | "repository" | "test" | "external" | "human";
  notes?: string;
}

export interface LearningAction {
  id: string;
  nodeId: string;
  reason: string;
  projectId?: string;
  task: string;
  completionCriteria: string[];
  evidenceRequired: string[];
  status: "queued" | "active" | "blocked" | "done";
}

export interface GraphStats {
  nodes: number;
  built: number;
  shipped: number;
  evidenced: number;
  activeProjects: number;
}

export interface GraphSnapshot {
  generatedAt: string;
  source: "d1" | "seed";
  nodes: TechnologyNode[];
  edges: GraphEdge[];
  projects: Project[];
  evidence: Evidence[];
  learningActions: LearningAction[];
  stats: GraphStats;
}

export interface EvaluationResult {
  nodeId: string;
  currentState: CapabilityState;
  recommendation: CapabilityState;
  eligible: boolean;
  reasons: string[];
  evidenceConsidered: number;
}
